import { YDebounced } from '../../y/debounced';
import { $debug, $log, xavis_experiments } from '../../y/debug';
import { $on, $trigger, YEventHandlers } from '../../y/events';
import { YStopWatch } from '../../y/stopwatch';
import { YTimer } from '../../y/timer';
import { yDefer, yLoadAsyncScript } from '../../y/util';
import { $el } from '../elements';
import { UIView } from '../view';

function makeTrack(src: string, lang: string, label: string, def: boolean) {
    const track = $el.create('track');
    track.kind = 'subtitles';
    track.src = src;
    track.srclang = lang;
    track.label = label;
    if(def) {
        track.default = true;
        // track.track.mode = 'showing';
    }

    return track;
}

type PlayState = 'play' | 'pause' | 'end';

abstract class Impl {
    video: HTMLVideoElement;
    player: UIHLSPlayer;
    playing: boolean;
    private _loaded: boolean;
    private _subs: SubData[];

    constructor(video: HTMLVideoElement, player: UIHLSPlayer) {
        this.video = video;
        this.player = player;
        this.playing = false;
        this._loaded = false;
        this._subs = [];

        const stop_watch = new YStopWatch;

        const timer = new YTimer(60);
        $on(timer, 'fired', () => {
            if(play_state.value == 'play')
                $trigger(player, 'played', stop_watch.reset());
        });

        const play_state = new YDebounced<PlayState>(1, 'pause');
        play_state.on_change = (from, to) => {
            if(to == 'play') {
                $trigger(player, 'play');
            } else if(from == 'play') {
                $trigger(player, to == 'end' ? 'ended' : 'pause', stop_watch.reset());
            }
        }

        const handler = (t: Event) => {
            const playing = video.readyState >= 3 && !video.paused && !video.ended;

            if(playing != this.playing) {
                this.playing = playing;
                $log('player', 'playing', playing);

                if(playing) {
                    stop_watch.start();
                    timer.start();
                } else {
                    stop_watch.stop();
                    timer.stop();
                }
            }

            if(t.type == 'ended')
                play_state.value = 'end';
            else if(t.type == 'play')
                play_state.value = 'play';
            else if(t.type == 'pause' && !video.seeking)
                play_state.value = video.ended ? 'end' : 'pause';
        }

        for(const t of ['canplay', 'canplaythrough', 'abort', 'ended', 'pause', 'play', 'playing', 'stalled', 'suspend', 'waiting']) {
            video.addEventListener(t, handler);
        }
    }

    set loaded(l: boolean) {
        if(l == this._loaded) return;

        this._loaded = l;

        if(l) {
            for(const s of this._subs) {
                $log('player', 'adding subtitles');
                this.video.ui_add(makeTrack(s.src, s.lang, s.label, s.def));
            }
        }
    }

    get loaded() {
        return this._loaded;
    }

    abstract load(url: string, play?: boolean): void;
    abstract play(): Promise<void>;
    abstract getPosition(): number;
    abstract setPosition(position: number): void;

    addSubtitles(src: string, lang: string, label: string, def: boolean) {
        this._subs.push({ src: src, lang: lang, label: label, def: def });

        if(this._loaded)
            this.video.ui_add(makeTrack(src, lang, label, def));
    }
}

interface SubData {
    src: string;
    lang: string;
    label: string;
    def: boolean;
}

class NativeImpl extends Impl {
    constructor(video: HTMLVideoElement, player: UIHLSPlayer) {
        super(video, player);
    }

    load(url: string, play?: boolean) {
        const video = this.video;

        this.loaded = false
        video.src = url;
        if(play)
            video.play();

        this.video.addEventListener('loadeddata', () => {
            this.loaded = true;
        });
    }

    play() {
        return this.video.play();
    }

    override getPosition() {
        return this.video.currentTime;
    }

    override setPosition(position: number) {
        this.video.currentTime = position;
    }
}

let hls_js_promise: Promise<void>;

function initHLSJS() {
    if(!hls_js_promise) {
        hls_js_promise = yLoadAsyncScript('//cdn.jsdelivr.net/npm/hls.js@latest');
    }

    return hls_js_promise;
}

class HlsJsImpl extends Impl {
    private _attached: Promise<Hls>;

    constructor(video: HTMLVideoElement, player: UIHLSPlayer) {
        super(video, player);

        const attached = yDefer<Hls>();
        this._attached = attached.promise;

        initHLSJS().then(() => {
            const hls = new Hls({ xhrSetup: xhr => { xhr.withCredentials = true } });
            hls.on(Hls.Events.MEDIA_ATTACHED, () => { attached.resolve(hls) });
            hls.attachMedia(video);

            if(xavis_experiments)
                $debug.hls_player = hls;
        });
    }

    load(url: string, play?: boolean) {
        this.loaded = false;

        this._attached.then(hls => {
            hls.loadSource(url);
            hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
                $log('player', 'manifest loaded', data);
                this.loaded = true;

                if(play)
                    this.video.play();
            });
        });
    }

    play() {
        return this.video.play();
    }

    override getPosition() {
        return this.video.currentTime;
    }

    override setPosition(position: number) {
        $log('player', 'setting current time to ', position);
        this.video.currentTime = position;
    }
}

let shaka_promise: Promise<void>;
declare const shaka: any;

function initShaka() {
    if(!shaka_promise) {
        shaka_promise = yLoadAsyncScript('https://cdn.jsdelivr.net/npm/mux.js@5.5.2/dist/mux.min.js').then(() => yLoadAsyncScript('//ajax.googleapis.com/ajax/libs/shaka-player/2.5.9/shaka-player.compiled.js'));
    }

    return shaka_promise;
}

class ShakaImpl extends Impl {
    private _attached: Promise<any>;

    constructor(video: HTMLVideoElement, player: UIHLSPlayer) {
        super(video, player);

        const attached = yDefer<any>();
        this._attached = attached.promise;

        this._attached = initShaka().then(() => {
            $log('player', 'inited shaka');
            const player = new shaka.Player(video);
            return player;
        })
    }

    load(url: string, _play?: boolean) {
        this._attached.then(player => {
            $log('player', 'got player', player);
            $log('player', 'loading url', url);
            player.load(url).then(() => {
                $log('player', 'yayyyyy');
            }, (err: any) => {
                $log('player', 'got error', err);
                $log('player', 'to string', err.toString());
            });
        })
    }

    play() {
        return this.video.play();
    }

    override getPosition() {
        return this.video.currentTime;
    }

    override setPosition(position: number) {
        this.video.currentTime = position;
    }
}

export class UIHLSPlayer extends UIView {
    $handlers: YEventHandlers<{ play: void } & { pause: number } & { ended: number } & { played: number }>;

    private _impl: Impl;

    constructor(link?: string, play?: boolean) {
        const video = $el.VIDEO();
        video.controls = true;
        video.crossOrigin = 'use-credentials';

        super('div', 'ui-player', video);

        if(xavis_experiments && false) {
            this._impl = new ShakaImpl(video, this);
        } else if(video.canPlayType('application/vnd.apple.mpegurl')) {
            this._impl = new NativeImpl(video, this);
        } else {
            this._impl = new HlsJsImpl(video, this);
        }

        // if(window.MediaSource) {
        //     this._impl = new HlsJsImpl(video, this);
        // } else {
        //     this._impl = new NativeImpl(video, this);
        // }

        if(link)
            this.load(link, play);
    }

    load(url: string, play?: boolean) {
        this._impl.load(url, play);
    }

    addSubtitles(src: string, lang: string, label: string, def: boolean) {
        this._impl.addSubtitles(src, lang, label, def);
    }

    play() {
        return this._impl.play();
    }

    get position() {
        return this._impl.getPosition();
    }

    set position(position: number) {
        this._impl.setPosition(position);
    }
}
